import type { TFunction } from 'i18next';
import { BuildingIcon, ShieldIcon, UsersIcon, type LucideIcon } from 'lucide-react';
import type { KnowledgeBaseSpaceVisibilityForm } from '../model';

/**
 * One icon and one label per visibility, so the create/edit sheet, the collection
 * header badge and the collections rail all name a visibility the same way.
 * `useKBVisibilityOptions` keeps its own MUI icons for the v3 field it still feeds.
 */
export const VISIBILITY_ICONS: Record<KnowledgeBaseSpaceVisibilityForm, LucideIcon> = {
  organization: BuildingIcon,
  workspace: UsersIcon,
  internal: ShieldIcon,
};

export const VISIBILITY_LABEL_KEYS = {
  organization: 'knowledgeBases.collectionsPanel.organization',
  workspace: 'knowledgeBases.collectionsPanel.workspace',
  internal: 'knowledgeBases.collectionsPanel.foundation',
} as const satisfies Record<KnowledgeBaseSpaceVisibilityForm, string>;

/**
 * Visibility is spread across two independent fields on the API's collection, and
 * foundation outranks a workspace id: a foundation collection created inside a
 * workspace still carries one.
 */
export function resolveCollectionVisibility(collection: {
  isInternal?: boolean;
  workspaceId?: string | null;
}): KnowledgeBaseSpaceVisibilityForm {
  if (collection.isInternal) return 'internal';
  if (collection.workspaceId) return 'workspace';
  return 'organization';
}

/**
 * What a collection's visibility badge says. A collection shared with several workspaces says how
 * many rather than naming one: `workspaceId` is only the first of them, so the count comes from
 * `workspaceCount`.
 */
export function getCollectionVisibilityLabel(
  t: TFunction<'knowledge'>,
  collection: { isInternal?: boolean; workspaceId?: string | null; workspaceCount?: number },
): string {
  const visibility = resolveCollectionVisibility(collection);
  const count = collection.workspaceCount ?? 0;
  if (visibility === 'workspace' && count > 1) {
    return t('knowledgeBases.collectionsPanel.workspacesCount', { count });
  }
  return t(VISIBILITY_LABEL_KEYS[visibility]);
}
